////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// ════════════════════════════════════════════════════════════════════════════════
// * POD - Data Processing Agreement - Logic and State Management
// * IRO Pattern: INPUT=none, REQUIREMENTS=manage DPA content state, OUTPUT=Pea with props
// ════════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import Pea_Dpa from '../pea/Bitsbound_Kings_Home_Primary_SRC_Dpa_Pea.js';

// ════════════════════════════════════════════════════════════════════════════════
// DEFINITIONS 'D' - DPA Content (GDPR Article 28 Compliant)
// ════════════════════════════════════════════════════════════════════════════════

const DPA_CONTENT = {
  overview: {
    title: "Data Processing Agreement Overview",
    content: `This Data Processing Agreement ("DPA") forms part of the Terms of Service between BitsBound, Inc. ("Processor," "we," "us") and the customer ("Controller," "you") who uses our AI-powered contract analysis platform.

This DPA applies to the processing of Personal Data by BitsBound on behalf of the Controller pursuant to GDPR Article 28 and other applicable data protection laws.

By using BitsBound services, you acknowledge and agree to this DPA.

Last Updated: December 21, 2025
Effective Date: December 21, 2025`,
  },
  definitions: {
    title: "Definitions",
    content: `"Personal Data" means any information relating to an identified or identifiable natural person contained within contracts you upload to BitsBound.

"Processing" means any operation performed on Personal Data, including collection, storage, analysis, modification, retrieval, consultation, use, disclosure, or erasure.

"Controller" means you, the customer who determines the purposes and means of Processing Personal Data by uploading contracts to BitsBound.

"Processor" means BitsBound, Inc., which processes Personal Data on your behalf.

"Sub-processor" means any third party engaged by BitsBound to process Personal Data on your behalf.

"Data Subject" means an identifiable natural person whose Personal Data is processed.

"Supervisory Authority" means an independent public authority established pursuant to GDPR Article 51.`,
  },
  "processing-details": {
    title: "Subject Matter and Details of Processing",
    content: `Nature and Purpose of Processing:
• Analyzing contract documents uploaded by you
• Extracting and processing text, including names, addresses, and other personal information contained in contracts
• Generating redlined documents with AI-suggested modifications
• Creating negotiation email drafts
• Storing analysis results for your reference

Types of Personal Data:
• Names of contracting parties and signatories
• Business contact information (email, phone, address)
• Professional titles and roles
• Any personal information contained within contract text

Categories of Data Subjects:
• Contracting parties and their representatives
• Signatories to contracts
• Third parties referenced in contract documents

Duration of Processing:
• Processing occurs during active analysis (typically 15-60 minutes per contract)
• Results retained while your account is active
• Data deleted upon account closure and completion of retention period`,
  },
  "processor-obligations": {
    title: "Processor Obligations (GDPR Article 28)",
    content: `BitsBound shall:

1. Process Personal Data only on documented instructions from you (Article 28(3)(a))
• We only process contract data you upload
• Processing limited to contract analysis purposes
• No processing for marketing or unrelated purposes

2. Ensure personnel confidentiality (Article 28(3)(b))
• All employees bound by confidentiality obligations
• Access limited to personnel requiring it for service delivery

3. Implement appropriate security measures (Article 28(3)(c))
• Encryption at rest (MongoDB Atlas)
• Encryption in transit (TLS/HTTPS)
• Customer data isolation (per-customer databases)
• Access controls and authentication

4. Respect sub-processor requirements (Article 28(3)(d))
• Written agreements with all sub-processors
• Sub-processors listed in this DPA
• Notification of sub-processor changes

5. Assist with Data Subject rights (Article 28(3)(e))
• Support your response to access requests
• Provide data export capabilities
• Enable data deletion on request

6. Assist with compliance obligations (Article 28(3)(f))
• Support security assessments
• Provide information for DPIAs
• Cooperate with supervisory authorities

7. Delete or return data on termination (Article 28(3)(g))
• Data deletion upon account closure
• Export option before deletion
• Certificate of deletion available on request

8. Demonstrate compliance (Article 28(3)(h))
• Audit rights granted to you
• Compliance documentation available
• Annual security reviews conducted`,
  },
  "sub-processors": {
    title: "Authorized Sub-processors",
    content: `BitsBound engages the following sub-processors:

Infrastructure:
• MongoDB Atlas (MongoDB, Inc.) - Database hosting
  Location: US or EU (your choice)
  Purpose: Secure storage of account and analysis data

• Cloudflare R2 (Cloudflare, Inc.) - Object storage
  Location: US
  Purpose: Contract file and playbook storage

• Vercel Inc. - Frontend hosting
  Location: US/Global CDN
  Purpose: Application delivery

• Render (Render Services, Inc.) - Backend hosting
  Location: US
  Purpose: API and analysis services

AI Processing:
• Anthropic - AI analysis
  Location: US
  Purpose: Contract analysis using Claude models
  Note: Your data is NOT used for model training

Payments:
• Stripe, Inc. - Payment processing
  Location: US
  Purpose: Subscription billing (PCI DSS Level 1)

Email:
• Resend - Transactional email
  Location: US
  Purpose: Account and service notifications

Sub-processor Changes:
We will notify you of any intended changes to sub-processors at least 30 days in advance. You may object to changes by contacting us.`,
  },
  "data-transfers": {
    title: "International Data Transfers",
    content: `Transfer Mechanisms:
Personal Data may be transferred outside the European Economic Area (EEA) to the United States. Such transfers are protected by:

• Standard Contractual Clauses (SCCs) with sub-processors
• Sub-processor certifications (where applicable)
• Your explicit choice of data residency (EU or US cluster)

EU Data Residency Option:
You may configure BitsBound to store your data exclusively in EU-based infrastructure by selecting the EU cluster during account setup.

Transfer Impact Assessment:
We have assessed the legal framework in destination countries and implemented supplementary measures where necessary to ensure adequate protection of Personal Data.

UK Transfers:
For UK-based Controllers, transfers are governed by the UK International Data Transfer Agreement (IDTA) or UK Addendum to the EU SCCs.`,
  },
  "security-measures": {
    title: "Technical and Organizational Measures",
    content: `Access Control:
• Role-based access control (RBAC)
• Multi-factor authentication available
• Password hashing with bcrypt (10+ salt rounds)
• JWT tokens with 7-day expiration

Encryption:
• TLS 1.2+ for all data in transit
• AES-256 encryption at rest (MongoDB Atlas)
• Secure credential storage (environment variables)

Data Isolation:
• Per-customer database architecture
• No commingling of customer data
• Logical separation of all customer environments

Monitoring and Logging:
• Audit logging of authentication events
• Failed login attempt monitoring
• Security event alerting

Infrastructure Security:
• SOC 2 compliant hosting providers
• Regular security updates and patches
• Network segmentation and firewalls

Incident Response:
• 24-hour breach notification to Controllers
• Documented incident response procedures
• Post-incident analysis and remediation`,
  },
  "data-subject-rights": {
    title: "Data Subject Rights Assistance",
    content: `BitsBound will assist you in responding to Data Subject requests:

Right of Access (Article 15):
• Export functionality for analysis data
• Provide copies of processed contract data

Right to Rectification (Article 16):
• Update account information
• Correct inaccurate analysis data

Right to Erasure (Article 17):
• Delete specific analyses
• Full account and data deletion

Right to Restriction (Article 18):
• Suspend processing on request
• Retain data without further processing

Right to Data Portability (Article 20):
• Export data in machine-readable format
• Transfer data to another processor

Right to Object (Article 21):
• Cease processing on valid objection
• Document and honor objections

Response Timeline:
We will respond to your assistance requests within 10 business days to enable your compliance with the 30-day Data Subject response deadline.`,
  },
  "breach-notification": {
    title: "Personal Data Breach Notification",
    content: `Notification to Controller:
BitsBound will notify you of any Personal Data breach without undue delay, and in any event within 24 hours of becoming aware of the breach.

Notification Content:
Our breach notification will include:
• Nature of the breach
• Categories and approximate number of Data Subjects affected
• Categories and approximate number of Personal Data records affected
• Name and contact details of our data protection contact
• Likely consequences of the breach
• Measures taken or proposed to address the breach

Assistance:
We will assist you in:
• Notifying the relevant supervisory authority (within 72 hours per GDPR Article 33)
• Communicating with affected Data Subjects (per GDPR Article 34)
• Investigating the breach and implementing remediation measures

Documentation:
We maintain records of all Personal Data breaches, including facts, effects, and remedial actions taken.`,
  },
  "audit-rights": {
    title: "Audit Rights",
    content: `Your Rights:
You have the right to audit our compliance with this DPA, subject to reasonable notice and confidentiality requirements.

Audit Methods:
• Review of compliance documentation
• Third-party audit reports (SOC 2, where available)
• On-site audits (with 30 days notice, during business hours)
• Remote security assessments

Audit Costs:
• Documentation reviews: No charge
• Third-party reports: No charge
• On-site audits: Reasonable costs may be charged

Frequency:
Audits may be conducted once per calendar year, unless a Personal Data breach or regulatory requirement necessitates additional audits.

Confidentiality:
Audit findings are confidential. Any third-party auditors must sign appropriate NDAs before accessing BitsBound facilities or documentation.`,
  },
  termination: {
    title: "Termination and Data Return",
    content: `Upon Termination:
At the end of the service relationship, BitsBound will:

1. Return Data (on request):
• Export all Personal Data in standard format
• Provide download links valid for 30 days
• Include all analyses, documents, and playbooks

2. Delete Data:
• Permanently delete all Personal Data within 90 days
• Delete from all systems including backups
• Provide deletion confirmation on request

Exceptions:
Data may be retained if required by applicable law. We will inform you of any such retention requirements.

Survival:
Confidentiality obligations survive termination indefinitely. Security measures remain in place until deletion is complete.`,
  },
  liability: {
    title: "Liability",
    content: `Processor Liability:
BitsBound is liable for damage caused by processing that violates this DPA or GDPR requirements applicable to Processors.

Controller Liability:
You remain liable for the lawfulness of your instructions and for ensuring a legal basis exists for processing Personal Data.

Indemnification:
Each party agrees to indemnify the other for claims, damages, and expenses arising from the indemnifying party's breach of this DPA or applicable data protection laws.

Limitation:
Total liability under this DPA is subject to the limitations set forth in the main Terms of Service, except where prohibited by applicable law.`,
  },
  contact: {
    title: "Contact Information",
    content: `Data Protection Contact:

BitsBound, Inc.
A Taytrom Company
Philadelphia, Pennsylvania

Email: admin@bitsbound.com
Website: https://www.bitsbound.com

For DPA-specific inquiries:
Subject line: "DPA Inquiry"

For Data Subject requests:
Subject line: "Data Subject Request"

Response Time:
We aim to respond to all DPA-related inquiries within 5 business days.`,
  },
};

// ════════════════════════════════════════════════════════════════════════════════
// DEFINITIONS 'D' - Section Icons
// ════════════════════════════════════════════════════════════════════════════════

const SECTION_ICONS: Record<string, string> = {
  overview: "📋",
  definitions: "📖",
  "processing-details": "⚙️",
  "processor-obligations": "✅",
  "sub-processors": "🔗",
  "data-transfers": "🌍",
  "security-measures": "🔐",
  "data-subject-rights": "✊",
  "breach-notification": "🚨",
  "audit-rights": "🔍",
  termination: "🔚",
  liability: "⚖️",
  contact: "📧",
};

// ════════════════════════════════════════════════════════════════════════════════
// FUNCTION - Pod_Dpa Component
// ════════════════════════════════════════════════════════════════════════════════

function Pod_Dpa() {
  // DECLARATIONS - State Management
  const [activeSection, setActiveSection] = useState("overview");

  // OPERATIONS - Handler Functions
  const handleSectionChange = useCallback((section: string) => {
    setActiveSection(section);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleDownload = useCallback(() => {
    const dpaText = Object.entries(DPA_CONTENT)
      .map(([_key, section]) => `${section.title}\n${'='.repeat(section.title.length)}\n${section.content}`)
      .join("\n\n");
    const blob = new Blob([dpaText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "BitsBound_Data_Processing_Agreement.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // PRE-RENDER - Page View Logging
  useEffect(() => {
    console.info("[DPA] Page viewed");
  }, []);

  // RENDER - Return Pea with Props
  return (
    <Pea_Dpa
      activeSection={activeSection}
      dpaContent={DPA_CONTENT}
      sectionIcons={SECTION_ICONS}
      onSectionChange={handleSectionChange}
      onPrint={handlePrint}
      onDownload={handleDownload}
    />
  );
}

// EXPORTS
export default Pod_Dpa;
